import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, formatTRY, todayISODate } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";

const CATEGORIES = ["Kira", "Elektrik", "Su", "Doğalgaz", "İnternet", "Vergi", "SGK", "Diğer"];
const CAT_COLOR = {
  "Kira": "bg-blue-50 text-blue-800 border-blue-200",
  "Elektrik": "bg-amber-50 text-amber-800 border-amber-200",
  "Su": "bg-cyan-50 text-cyan-800 border-cyan-200",
  "Doğalgaz": "bg-orange-50 text-orange-800 border-orange-200",
  "İnternet": "bg-violet-50 text-violet-800 border-violet-200",
  "Vergi": "bg-rose-50 text-rose-800 border-rose-200",
  "SGK": "bg-pink-50 text-pink-800 border-pink-200",
  "Diğer": "bg-slate-100 text-slate-800 border-slate-200",
};
const emptyForm = { name: "", category: "Kira", amount: 0, paid_amount: 0, fully_paid: true, date: todayISODate(), note: "" };

export default function ShopExpenses() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => setItems((await api.get("/shop-expenses")).data);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => {
    const total = Number(s.amount || 0);
    const paid = s.paid_amount == null ? total : Number(s.paid_amount);
    setEditing(s);
    setForm({
      name: s.name, category: s.category, amount: total,
      paid_amount: paid,
      fully_paid: paid >= total - 0.01,
      date: (s.date || todayISODate()).slice(0, 10), note: s.note || "",
    });
    setOpen(true);
  };

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "amount" && prev.fully_paid) {
        next.paid_amount = Number(value) || 0;
      }
      if (field === "fully_paid" && value) {
        next.paid_amount = Number(prev.amount) || 0;
      }
      return next;
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Gider adı gerekli"); return; }
    const total = Number(form.amount) || 0;
    const paid = form.fully_paid ? total : Math.max(0, Math.min(Number(form.paid_amount) || 0, total));
    const payload = {
      name: form.name,
      category: form.category,
      amount: total,
      paid_amount: paid,
      date: form.date,
      note: form.note,
    };
    if (editing) { await api.put(`/shop-expenses/${editing.id}`, payload); toast.success("Güncellendi"); }
    else { await api.post("/shop-expenses", payload); toast.success("Eklendi"); }
    setOpen(false); load();
  };
  const del = async () => { await api.delete(`/shop-expenses/${deleteId}`); toast.success("Silindi"); setDeleteId(null); load(); };

  const filtered = items.filter((s) => {
    const t = q.toLowerCase();
    const matchQ = !q || s.name?.toLowerCase().includes(t) || s.note?.toLowerCase().includes(t);
    const matchCat = catFilter === "all" || s.category === catFilter;
    return matchQ && matchCat;
  });

  const monthTotal = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear(); const m = now.getMonth();
    return items.filter((s) => {
      const d = new Date(s.date);
      return d.getFullYear() === y && d.getMonth() === m;
    }).reduce((sum, s) => sum + (s.amount || 0), 0);
  }, [items]);

  const totalDebt = useMemo(() => {
    return items.reduce((sum, s) => {
      const total = Number(s.amount) || 0;
      const paid = s.paid_amount == null ? total : Number(s.paid_amount) || 0;
      return sum + Math.max(total - paid, 0);
    }, 0);
  }, [items]);

  return (
    <div data-testid="shop-expenses-page" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Bu Ay Dükkan Gideri</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="month-shop-total">{formatTRY(monthTotal)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Toplam Dükkan Gideri</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num">
            {formatTRY(items.reduce((s, x) => s + (x.amount || 0), 0))}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Kalem Sayısı</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num">{items.length}</p>
        </div>
      </div>

      {totalDebt > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center justify-between" data-testid="shop-debt-alert">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold">Ödenmemiş Dükkan Gideri</p>
            <p className="text-xs text-rose-800 mt-0.5">Kısmi ödenen veya ödenmemiş dükkan giderlerinin kalan tutarı</p>
          </div>
          <p className="font-display text-2xl font-bold text-rose-700 font-mono-num">{formatTRY(totalDebt)}</p>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          <div className="relative w-full max-w-xs">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="shop-expenses-search" placeholder="Ara…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="shop-cat-filter" className="w-44 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="add-shop-expense-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Gider
        </Button>
      </div>

      <TableWrap>
        <Thead columns={["Tarih", "Gider Adı", "Kategori", "Tutar", "Not", ""]} />
        <tbody>
          {filtered.length === 0 && <EmptyRow colSpan={6} testId="shop-expenses-empty" message="Henüz dükkan gideri yok. Kira, elektrik, su vb. giderlerinizi ekleyin." />}
          {filtered.map((s) => (
            <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`shop-expense-row-${s.id}`}>
              <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{formatDate(s.date)}</td>
              <td className="px-4 py-3 font-semibold text-slate-900">{s.name}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${CAT_COLOR[s.category] || CAT_COLOR["Diğer"]}`}>{s.category}</span>
              </td>
              <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900">
                {formatTRY(s.amount)}
                {(() => {
                  const paid = s.paid_amount == null ? s.amount : Number(s.paid_amount);
                  const remaining = Math.max((Number(s.amount) || 0) - paid, 0);
                  if (remaining > 0.009) {
                    return (
                      <div className="text-[10px] font-normal text-rose-700 mt-0.5">
                        Ödenmemiş: {formatTRY(remaining)}
                      </div>
                    );
                  }
                  return null;
                })()}
              </td>
              <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{s.note || "-"}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button data-testid={`edit-shop-expense-${s.id}`} variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-8 w-8">
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button data-testid={`delete-shop-expense-${s.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(s.id)} className="h-8 w-8 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shop-expense-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Gideri Düzenle" : "Yeni Dükkan Gideri"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Gider Adı</Label>
              <Input data-testid="shop-expense-name-input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Örn: Kira Ocak, Elektrik Faturası" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger data-testid="shop-expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tutar (₺)</Label>
                <Input data-testid="shop-expense-amount-input" type="number" step="0.01" value={form.amount} onChange={(e) => setField("amount", e.target.value)} className="font-mono-num" />
              </div>
            </div>
            <div>
              <Label>Tarih</Label>
              <Input data-testid="shop-expense-date-input" type="date" value={form.date?.slice(0, 10)} onChange={(e) => setField("date", e.target.value)} />
            </div>

            {/* Ödeme Durumu */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <input
                  data-testid="shop-fully-paid-checkbox"
                  type="checkbox"
                  id="shop-fully-paid"
                  checked={form.fully_paid}
                  onChange={(e) => setField("fully_paid", e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <Label htmlFor="shop-fully-paid" className="text-sm cursor-pointer">
                  Tamamı ödendi
                </Label>
              </div>
              {!form.fully_paid && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      Ödenen Tutar (₺)
                    </Label>
                    <Input
                      data-testid="shop-paid-input"
                      type="number"
                      step="0.01"
                      value={form.paid_amount}
                      onChange={(e) => setField("paid_amount", e.target.value)}
                      className="font-mono-num h-9 text-sm bg-white"
                      placeholder="₺"
                    />
                  </div>
                  <div className="flex flex-col justify-end">
                    <p className="text-[11px] text-slate-500">Kalan</p>
                    <p className="font-mono-num font-bold text-rose-700 text-base">
                      ₺{Math.max((Number(form.amount) || 0) - (Number(form.paid_amount) || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Not</Label>
              <Input data-testid="shop-expense-note-input" value={form.note} onChange={(e) => setField("note", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-shop-expense-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gider silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-shop-expense" onClick={del} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
